import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Empty, Input, Modal, Segmented, Space, Spin, Tag, Typography, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { fetchExport, fetchTeamMembers } from "./attendanceApi";
import { saveFile } from "../../../native/files";

const { Text } = Typography;
const DATE = "YYYY-MM-DD";

/**
 * Team export of one month: choose the format and who goes in (default: everyone). People
 * without entries end up only in the overview of the file.
 */
const TeamExportModal = ({ open, month, initialFormat = "pdf", onClose }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [format, setFormat] = useState(initialFormat);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const from = month.startOf("month").format(DATE);
  const to = month.endOf("month").format(DATE);

  useEffect(() => {
    if (!open) return;
    setFormat(initialFormat);
    setSearch("");
    setLoading(true);
    fetchTeamMembers(from, to)
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setMembers(list);
        setSelected(list.map(m => m._id));
      })
      .catch(error => message.error(t(error.response?.data?.message || "Failed to load the team")))
      .finally(() => setLoading(false));
  }, [open, from, to]);

  const visible = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return members.filter(m =>
      words.every(w => `${m.name} ${m.personnelNo}`.toLowerCase().includes(w))
    );
  }, [members, search]);

  const toggle = (id, on) =>
    setSelected(prev => (on ? [...new Set([...prev, id])] : prev.filter(x => x !== id)));
  const setVisible = on => {
    const ids = visible.map(m => m._id);
    setSelected(prev => (on ? [...new Set([...prev, ...ids])] : prev.filter(x => !ids.includes(x))));
  };

  const run = async () => {
    setExporting(true);
    try {
      const lng = (i18n.language || "de").slice(0, 2);
      const all = selected.length === members.length;
      const blob = await fetchExport("team", {
        from,
        to,
        format,
        lng,
        employees: all ? undefined : selected
      });
      const base = lng === "de" ? "Arbeitszeitnachweise_Team" : "Timesheets_Team";
      await saveFile(blob, `${base}_${month.format("YYYY-MM")}.${format}`);
      onClose();
    } catch (error) {
      const text = error.response?.data?.message;
      message.error(text ? t(text) : t("Export failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`${t("Team export")} · ${month.format("MMMM YYYY")}`}
      width={560}
      rootClassName="attendance-team-export"
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button
          key="run"
          type="primary"
          loading={exporting}
          disabled={!selected.length || loading}
          onClick={run}
        >
          {t("Export {{count}} people", { count: selected.length })}
        </Button>
      ]}
    >
      <div className="attendance-team-export__format">
        <Segmented
          block
          value={format}
          onChange={setFormat}
          options={[
            { value: "pdf", label: "PDF" },
            { value: "xlsx", label: "Excel" },
            { value: "csv", label: "CSV" }
          ]}
        />
      </div>

      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder={t("Search name or personnel number")}
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <Space wrap className="attendance-team-export__bulk">
        <Button size="small" onClick={() => setVisible(true)}>
          {t("Select all")}
        </Button>
        <Button size="small" onClick={() => setVisible(false)}>
          {t("Select none")}
        </Button>
        <Button
          size="small"
          onClick={() => setSelected(members.filter(m => m.hasTimes).map(m => m._id))}
        >
          {t("Only people with times")}
        </Button>
        <Text type="secondary">
          {t("{{selected}} of {{total}} selected", {
            selected: selected.length,
            total: members.length
          })}
        </Text>
      </Space>

      {loading ? (
        <div className="attendance-team-export__loading">
          <Spin />
        </div>
      ) : visible.length ? (
        <ul className="attendance-team-export__list">
          {visible.map(m => (
            <li key={m._id}>
              <Checkbox
                checked={selected.includes(m._id)}
                onChange={e => toggle(m._id, e.target.checked)}
              >
                <span className="attendance-team-export__name">{m.name}</span>
                {m.personnelNo && <Text type="secondary"> · {m.personnelNo}</Text>}
              </Checkbox>
              <span className="attendance-team-export__tags">
                {m.owner && <Tag color="blue">{t("Owner")}</Tag>}
                {m.deleted && <Tag>{t("Left the company")}</Tag>}
                {!m.hasTimes && <Tag>{t("No times")}</Tag>}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Modal>
  );
};

export default TeamExportModal;
